import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Handle, Position } from "@xyflow/react";
import VerticalActionRegistryService from "../../services/VerticalActionRegistryService";

const ROUTER_MODES = [
  {
    id: "action_result",
    label: "Action Result",
    description: "Route based on the outcome of the previous action, API, booking, or payment step.",
  },
  {
    id: "message",
    label: "Customer Message",
    description: "Route based on text, button/list replies, or uploaded media from the customer.",
  },
  {
    id: "state",
    label: "State / Variable",
    description: "Route based on saved answers, cart/order values, contact fields, or session state.",
  },
  {
    id: "event",
    label: "Event",
    description: "Route based on platform events such as payment success, booking confirmed, or reminders.",
  },
  {
    id: "ai_intent",
    label: "AI Intent",
    description: "Future-ready routing from AI classification, sentiment, or extracted entities.",
  },
];

const OPERATORS = [
  { value: "equals", label: "equal" },
  { value: "not_equals", label: "not equal" },
  { value: "contains", label: "contains" },
  { value: "exists", label: "exists" },
  { value: "not_exists", label: "does not exist" },
  { value: "greater_than", label: "greater than" },
  { value: "less_than", label: "less than" },
];

const SOURCE_OPTIONS = [
  { value: "previous_action", label: "Previous action result" },
  { value: "api_response", label: "API / webhook response" },
  { value: "payment", label: "Payment" },
  { value: "message", label: "Customer message" },
  { value: "interactive_reply", label: "Button / list reply" },
  { value: "state", label: "Saved variable / state" },
  { value: "contact", label: "CRM / contact field" },
  { value: "event", label: "Domain event" },
  { value: "ai", label: "AI intent" },
];

const FIELD_OPTIONS = {
  previous_action: [
    { value: "success", label: "Action succeeded?" },
    { value: "status", label: "Action status" },
    { value: "error_code", label: "Action error / outcome" },
  ],
  api_response: [
    { value: "status_code", label: "HTTP status code" },
    { value: "error_code", label: "Response error / outcome" },
    { value: "body.status", label: "Response status" },
  ],
  payment: [
    { value: "status", label: "Payment status" },
    { value: "provider", label: "Payment provider" },
    { value: "amount", label: "Payment amount" },
  ],
  message: [
    { value: "text", label: "Message text" },
    { value: "type", label: "Message type" },
    { value: "has_media", label: "Has media uploaded?" },
  ],
  interactive_reply: [
    { value: "button_id", label: "Button ID" },
    { value: "button_title", label: "Button title" },
    { value: "list_id", label: "List row ID" },
    { value: "list_title", label: "List row title" },
  ],
  state: [
    { value: "cart.total", label: "Cart total" },
    { value: "cart.has_orphans", label: "Has items from an earlier visit" },
    { value: "cart.item_count", label: "Cart item count (this visit)" },
    { value: "cart.orphan_count", label: "Old cart item count" },
    { value: "cart.orphan_total", label: "Old cart total value" },
    { value: "order.status", label: "Order status" },
    { value: "customer.city", label: "Customer city" },
    { value: "flow.answer", label: "Flow answer" },
  ],
  contact: [
    { value: "city", label: "City" },
    { value: "tags", label: "Tags" },
    { value: "segment", label: "Segment" },
  ],
  event: [
    { value: "type", label: "Event type" },
    { value: "status", label: "Event status" },
  ],
  ai: [
    { value: "intent", label: "Intent" },
    { value: "confidence", label: "Confidence" },
    { value: "sentiment", label: "Sentiment" },
  ],
};

const VISIBLE_TEMPLATE_KEYS = [
  "customer_message",
  "order_cart",
  "payment",
  "ticket_hold",
  "appointment_hold",
];

/** Presets hidden during chatbot setup; still shown if this flow already uses the profile. */
const WORKFLOW_ROUTER_PRESETS_HIDDEN_UNLESS_ACTIVE = new Set([
  "button_selection",
  "message_intent",
]);

const COMMON_TEMPLATES = {
  generic_action: {
    label: "Previous step",
    description: "Use after any action, webhook, booking, order, or payment preparation step.",
    mode: "action_result",
    branches: [
      {
        id: "success",
        label: "Completed",
        description: "Previous action completed successfully.",
        conditions: [{ source: "previous_action", field: "success", operator: "equals", value: "true" }],
      },
      {
        id: "failed",
        label: "Could not complete",
        description: "Previous action failed or returned an error.",
        conditions: [{ source: "previous_action", field: "success", operator: "equals", value: "false" }],
      },
    ],
  },
  customer_message: {
    label: "Customer message",
    description: "Route based on what the customer typed, selected, or uploaded.",
    mode: "message",
    branches: [
      {
        id: "has_reply",
        label: "Customer replied",
        description: "Customer sent any message.",
        conditions: [{ source: "message", field: "text", operator: "exists", value: "" }],
      },
      {
        id: "media_uploaded",
        label: "Media uploaded",
        description: "Customer uploaded an image, document, audio, or video.",
        conditions: [{ source: "message", field: "has_media", operator: "equals", value: "true" }],
      },
    ],
  },
  button_selection: {
    label: "Button / list selection",
    description: "Route based on button or list option selected by customer.",
    mode: "message",
    branches: [
      {
        id: "button_title",
        label: "Selected button text",
        description: "Customer selected a button with matching title.",
        conditions: [{ source: "interactive_reply", field: "button_title", operator: "contains", value: "Yes" }],
      },
      {
        id: "list_title",
        label: "Selected list option",
        description: "Customer selected a list option with matching title.",
        conditions: [{ source: "interactive_reply", field: "list_title", operator: "contains", value: "Option" }],
      },
    ],
  },
  order_cart: {
    label: "Order / cart",
    description: "Add Cart: Refresh cart before this router. Branch order matters (top = first). Put Ready for address LAST.",
    mode: "state",
    branches: [
      {
        id: "cart_stock_check_failed",
        label: "Stock check failed (retry)",
        description: "Store inventory could not be verified (peak load/timeout). Loop to Cart: Refresh cart — do not send to address.",
        conditions: [{ source: "state", field: "cart.stock_check_failed", operator: "equals", value: "true" }],
      },
      {
        id: "cart_has_orphans",
        label: "Has old cart items",
        description: "Customer still has products left from a previous visit or abandoned cart.",
        conditions: [{ source: "state", field: "cart.has_orphans", operator: "equals", value: "true" }],
      },
      {
        id: "cart_has_invalid_items",
        label: "Has out-of-stock items",
        description: "Current cart has unavailable or insufficient stock (use {{invaliditems}} in message).",
        conditions: [{ source: "state", field: "cart.has_invalid_items", operator: "equals", value: "true" }],
      },
      {
        id: "cart_empty",
        label: "Cart is empty",
        description: "No products in current cart (after removals).",
        conditions: [{ source: "state", field: "cart.has_items", operator: "equals", value: "false" }],
      },
      {
        id: "cart_ready_for_address",
        label: "Ready for address",
        description: "All checks passed — no stock error, no orphans, no OOS, has items. Connect to address flow.",
        conditions: [
          { source: "state", field: "cart.stock_check_failed", operator: "equals", value: "false" },
          { source: "state", field: "cart.has_orphans", operator: "equals", value: "false" },
          { source: "state", field: "cart.has_invalid_items", operator: "equals", value: "false" },
          { source: "state", field: "cart.has_items", operator: "equals", value: "true" },
        ],
      },
      {
        id: "cart_has_items",
        label: "Cart has items",
        description: "Customer has products in cart.",
        conditions: [{ source: "state", field: "cart.total", operator: "greater_than", value: "0" }],
      },
      {
        id: "cart_no_orphans",
        label: "Only current cart (legacy — do not use before OOS)",
        description: "Unsafe alone: matches before out-of-stock. Use Ready for address instead.",
        conditions: [{ source: "state", field: "cart.has_orphans", operator: "equals", value: "false" }],
      },
      {
        id: "order_created",
        label: "Order created",
        description: "Order was created successfully.",
        conditions: [{ source: "state", field: "order.status", operator: "exists", value: "" }],
      },
      {
        id: "high_value_cart",
        label: "High value cart",
        description: "Cart total is greater than the configured amount.",
        conditions: [{ source: "state", field: "cart.total", operator: "greater_than", value: "1000" }],
      },
    ],
  },
  payment: {
    label: "Payment",
    description: "Route based on payment status or payment preparation.",
    mode: "action_result",
    branches: [
      {
        id: "paid",
        label: "Payment Successful",
        description: "Payment is completed.",
        conditions: [{ source: "payment", field: "status", operator: "equals", value: "paid" }],
      },
      {
        id: "pending",
        label: "Payment Pending",
        description: "Payment is waiting or processing.",
        conditions: [{ source: "payment", field: "status", operator: "equals", value: "pending" }],
      },
      {
        id: "failed",
        label: "Payment Failed",
        description: "Payment failed, expired, or was cancelled.",
        conditions: [{ source: "payment", field: "status", operator: "not_equals", value: "paid" }],
      },
    ],
  },
  ticket_hold: {
    label: "Ticket / reservation",
    description: "Route after ticket or reservation availability/hold actions.",
    mode: "action_result",
    branches: [
      {
        id: "success",
        label: "Success",
        description: "Seats/inventory were held successfully.",
        conditions: [{ source: "previous_action", field: "success", operator: "equals", value: "true" }],
      },
      {
        id: "seat_unavailable",
        label: "Seat Already Taken",
        description: "Selected seat is already held or booked.",
        conditions: [{ source: "previous_action", field: "error_code", operator: "equals", value: "SEAT_UNAVAILABLE" }],
      },
      {
        id: "capacity",
        label: "Sold Out",
        description: "Requested tier/trip has insufficient capacity.",
        conditions: [{ source: "previous_action", field: "error_code", operator: "equals", value: "CAPACITY" }],
      },
      {
        id: "hold_expired",
        label: "Hold Expired",
        description: "Reservation hold expired before completion.",
        conditions: [{ source: "previous_action", field: "error_code", operator: "equals", value: "HOLD_EXPIRED" }],
      },
      {
        id: "validation",
        label: "Missing Details",
        description: "The request needs corrected passenger, seat, or trip details.",
        conditions: [{ source: "previous_action", field: "error_code", operator: "equals", value: "VALIDATION" }],
      },
    ],
  },
  appointment_hold: {
    label: "Appointment",
    description: "Route after appointment availability/slot hold actions.",
    mode: "action_result",
    branches: [
      {
        id: "success",
        label: "Success",
        description: "Appointment slot was held successfully.",
        conditions: [{ source: "previous_action", field: "success", operator: "equals", value: "true" }],
      },
      {
        id: "overlap",
        label: "Slot Already Booked",
        description: "Selected slot conflicts with an existing booking.",
        conditions: [{ source: "previous_action", field: "error_code", operator: "equals", value: "OVERLAP" }],
      },
      {
        id: "held",
        label: "Temporarily Held",
        description: "Selected slot is currently held by another customer.",
        conditions: [{ source: "previous_action", field: "error_code", operator: "equals", value: "HELD" }],
      },
      {
        id: "resource_mismatch",
        label: "Wrong Staff / Resource",
        description: "Selected resource cannot perform this service.",
        conditions: [{ source: "previous_action", field: "error_code", operator: "equals", value: "RESOURCE_TYPE_MISMATCH" }],
      },
      {
        id: "hold_expired",
        label: "Hold Expired",
        description: "Appointment hold expired before completion.",
        conditions: [{ source: "previous_action", field: "error_code", operator: "equals", value: "HOLD_EXPIRED" }],
      },
    ],
  },
  message_intent: {
    label: "Message keywords",
    description: "Route based on common support keywords in customer messages.",
    mode: "message",
    branches: [
      {
        id: "cancel",
        label: "Customer Wants Cancel",
        description: "Customer message contains cancel intent.",
        conditions: [{ source: "message", field: "text", operator: "contains", value: "cancel" }],
      },
      {
        id: "refund",
        label: "Customer Asks Refund",
        description: "Customer message contains refund.",
        conditions: [{ source: "message", field: "text", operator: "contains", value: "refund" }],
      },
      {
        id: "media",
        label: "Media Uploaded",
        description: "Customer uploaded an image, document, audio, or video.",
        conditions: [{ source: "message", field: "has_media", operator: "equals", value: "true" }],
      },
    ],
  },
  ecommerce_state: {
    label: "CRM state",
    description: "Route using saved variables, contact fields, or segments.",
    mode: "state",
    branches: [
      {
        id: "high_value_cart",
        label: "High Value Cart",
        description: "Cart total is greater than the configured amount.",
        conditions: [{ source: "state", field: "cart.total", operator: "greater_than", value: "1000" }],
      },
      {
        id: "city_chennai",
        label: "Customer in Chennai",
        description: "Customer city is Chennai.",
        conditions: [{ source: "contact", field: "city", operator: "equals", value: "Chennai" }],
      },
    ],
  },
};

const DEFAULT_ROUTER = {
  routerMode: "message",
  actionProfile: "customer_message",
  logic: "first_match",
  defaultLabel: "Default / fallback",
  branches: COMMON_TEMPLATES.customer_message.branches,
};

const slug = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "_")
  .replace(/^_+|_+$/g, "")
  || `branch_${Date.now()}`;

const newBranch = () => ({
  id: `branch_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  label: "New branch",
  description: "",
  match: "all",
  conditions: [{ source: "previous_action", field: "success", operator: "equals", value: "true" }],
});

const materializeTemplateBranches = (branches = []) => branches.map((branch, index) => normalizeBranch({
  ...branch,
  id: `${branch.id || "branch"}_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 6)}`,
}, index));

const normalizeBranch = (branch, index = 0) => ({
  id: branch?.id || branch?.branch_key || slug(branch?.label || `branch_${index + 1}`),
  label: branch?.label || `Branch ${index + 1}`,
  description: branch?.description || "",
  match: branch?.match || "all",
  conditions: Array.isArray(branch?.conditions) && branch.conditions.length > 0
    ? branch.conditions.map((condition) => ({
      source: condition.source || "state",
      field: condition.field || condition.variable || "last_action_success",
      operator: condition.operator || "equals",
      value: condition.value ?? "",
    }))
    : [{
      source: "state",
      field: branch?.variable || "last_action_success",
      operator: branch?.operator || "equals",
      value: branch?.value ?? "",
    }],
});

const ensureUniqueBranchIds = (branches = []) => {
  const seen = new Set();
  return branches.map((branch, index) => {
    const baseId = branch.id || slug(branch.label || `branch_${index + 1}`);
    const nextId = seen.has(baseId)
      ? `${baseId}_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 6)}`
      : baseId;
    seen.add(nextId);
    return { ...branch, id: nextId };
  });
};

const normalizeRouter = (data = {}) => {
  if (Array.isArray(data.branches) && data.branches.length > 0) {
    return {
      routerMode: data.routerMode || "action_result",
      actionProfile: data.actionProfile || "generic_action",
      logic: data.logic || "first_match",
      defaultLabel: data.defaultLabel || "Default / fallback",
      branches: ensureUniqueBranchIds(data.branches.map(normalizeBranch)),
    };
  }

  if (Array.isArray(data.conditions) && data.conditions.length > 0) {
    return {
      ...DEFAULT_ROUTER,
      branches: ensureUniqueBranchIds(data.conditions.map(normalizeBranch)),
    };
  }

  return {
    ...DEFAULT_ROUTER,
    branches: materializeTemplateBranches(DEFAULT_ROUTER.branches),
  };
};

const branchSummary = (branch) => {
  const condition = branch?.conditions?.[0];
  if (!condition) return "No condition configured";
  const source = SOURCE_OPTIONS.find((item) => item.value === condition.source)?.label || condition.source;
  const field = FIELD_OPTIONS[condition.source]?.find((item) => item.value === condition.field)?.label || condition.field;
  const operator = OPERATORS.find((item) => item.value === condition.operator)?.label || condition.operator;
  if (["exists", "not_exists"].includes(condition.operator)) return `${source}: ${field} ${operator}`;
  return `${source}: ${field} ${operator} ${condition.value}`;
};

const conditionNeedsValue = (operator) => !["exists", "not_exists"].includes(operator);

const conditionKey = (condition = {}) => [
  condition.source || "",
  condition.field || "",
  condition.operator || "",
  condition.value ?? "",
].join("|");

const branchConditionKey = (branch) => conditionKey(branch?.conditions?.[0]);

export const ConditionMessageNode = ({ data, id, selected = false }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [router, setRouter] = useState(normalizeRouter(data));
  const [draft, setDraft] = useState(router);
  const [error, setError] = useState("");
  const [registry, setRegistry] = useState(null);
  const [loadingRegistry, setLoadingRegistry] = useState(false);
  const [showAdvancedRules, setShowAdvancedRules] = useState(false);
  const menuRef = useRef(null);

  const branchLayout = useMemo(() => {
    const paddingTop = 10;
    const headerBlock = 48;
    const rowSlot = 28;
    const branchCount = router.branches.length;
    const rowTop = (index) => paddingTop + headerBlock + (index * rowSlot) + (rowSlot / 2);
    return {
      rowSlot,
      minHeight: paddingTop + headerBlock + (branchCount + 1) * rowSlot + 12,
      branchHandleTop: rowTop,
      defaultHandleTop: () => rowTop(branchCount),
    };
  }, [router.branches.length]);

  const branchHandleStyle = (topPx, color = "#7c3aed") => ({
    position: "absolute",
    top: topPx,
    right: -7,
    width: 12,
    height: 12,
    transform: "none",
    zIndex: 10,
    background: color,
    border: "2px solid #fff",
    boxShadow: "0 0 0 1px rgba(124, 58, 237, 0.35)",
  });

  const incomingSource = useMemo(() => {
    const edges = Array.isArray(data.edges) ? data.edges : [];
    const nodes = Array.isArray(data.workflowNodes) ? data.workflowNodes : [];
    const incoming = edges.find((edge) => edge.target === id);
    return incoming ? nodes.find((node) => node.id === incoming.source) : null;
  }, [data.edges, data.workflowNodes, id]);

  const variableOptions = useMemo(() => Array.from(new Set([
    ...((Array.isArray(data.availableVariables) ? data.availableVariables : [])
      .map((value) => String(value || "").trim())
      .filter(Boolean)),
    "cart.total",
    "cart.has_orphans",
    "cart.item_count",
    "cart.orphan_count",
    "cart.orphan_total",
    "order.status",
    "customer.city",
    "payment.status",
    "last_action_success",
    "last_error_code",
  ])), [data.availableVariables]);

  useEffect(() => {
    setRouter(normalizeRouter(data));
  }, [data.routerMode, data.actionProfile, data.branches, data.conditions, data.defaultLabel]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setShowMenu(false);
    };
    if (showMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  const syncRouter = (nextRouter) => {
    setRouter(nextRouter);
    if (data.onChangeRouter) data.onChangeRouter(id, nextRouter);
    if (data.onChangeConditions) {
      data.onChangeConditions(id, nextRouter.branches.map((branch) => ({
        label: branch.label,
        variable: branch.conditions?.[0]?.field || "last_action_success",
        operator: branch.conditions?.[0]?.operator || "equals",
        value: branch.conditions?.[0]?.value || "",
      })));
    }
  };

  const openEdit = async () => {
    const current = normalizeRouter(data);
    setDraft(current);
    setError("");
    setShowEditPopup(true);
    setShowMenu(false);
    try {
      setLoadingRegistry(true);
      const nextRegistry = await VerticalActionRegistryService.actionRegistry();
      setRegistry(nextRegistry || null);
    } catch (_error) {
      setRegistry(null);
    } finally {
      setLoadingRegistry(false);
    }
  };

  const updateDraft = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const applyTemplate = (templateKey) => {
    const template = COMMON_TEMPLATES[templateKey] || COMMON_TEMPLATES.generic_action;
    setDraft((prev) => ({
      ...prev,
      routerMode: template.mode,
      actionProfile: templateKey,
      branches: materializeTemplateBranches(template.branches),
    }));
  };

  const applyBranchPreset = (branchIndex, presetIndex) => {
    const template = COMMON_TEMPLATES[draft.actionProfile] || COMMON_TEMPLATES.generic_action;
    const preset = template.branches[Number(presetIndex)];
    if (!preset) return;
    updateBranch(branchIndex, {
      label: preset.label,
      description: preset.description,
      match: preset.match || "all",
      conditions: preset.conditions,
    });
  };

  const updateBranch = (branchIndex, patch) => {
    setDraft((prev) => ({
      ...prev,
      branches: prev.branches.map((branch, index) => (
        index === branchIndex ? normalizeBranch({ ...branch, ...patch }, index) : branch
      )),
    }));
  };

  const updateCondition = (branchIndex, conditionIndex, patch) => {
    setDraft((prev) => ({
      ...prev,
      branches: prev.branches.map((branch, bIndex) => {
        if (bIndex !== branchIndex) return branch;
        const conditions = branch.conditions.map((condition, cIndex) => (
          cIndex === conditionIndex ? { ...condition, ...patch } : condition
        ));
        return { ...branch, conditions };
      }),
    }));
  };

  const addCondition = (branchIndex) => {
    setDraft((prev) => ({
      ...prev,
      branches: prev.branches.map((branch, index) => (
        index === branchIndex
          ? {
            ...branch,
            conditions: [
              ...branch.conditions,
              { source: "state", field: "cart.total", operator: "greater_than", value: "1000" },
            ],
          }
          : branch
      )),
    }));
  };

  const addOutputBranch = () => {
    const template = COMMON_TEMPLATES[draft.actionProfile] || COMMON_TEMPLATES.customer_message;
    const preset = template.branches[0] || newBranch();
    setDraft((prev) => ({
      ...prev,
      branches: [
        ...prev.branches,
        materializeTemplateBranches([preset])[0],
      ],
    }));
  };

  const validateDraft = () => {
    if (!draft.branches.length) return "Add at least one output branch.";
    const branchIds = new Set();
    for (const branch of draft.branches) {
      if (!String(branch.label || "").trim()) return "Every branch needs a label.";
      if (branchIds.has(branch.id)) return "Branch labels must be unique.";
      branchIds.add(branch.id);
      for (const condition of branch.conditions || []) {
        if (!condition.source || !condition.field || !condition.operator) {
          return "Every branch condition needs a source, field, and operator.";
        }
        if (conditionNeedsValue(condition.operator) && !String(condition.value || "").trim()) {
          return "Every branch condition needs a value unless the operator is exists/does not exist.";
        }
      }
    }
    return "";
  };

  const handleSave = () => {
    const validationError = validateDraft();
    if (validationError) {
      setError(validationError);
      return;
    }
    syncRouter({
      ...draft,
      branches: ensureUniqueBranchIds(draft.branches.map(normalizeBranch)),
    });
    setShowEditPopup(false);
  };

  const registryTemplates = useMemo(() => {
    const appointmentCodes = Object.entries(registry?.error_codes?.appointment || {});
    const ticketCodes = Object.entries(registry?.error_codes?.ticket || {});
    return { appointmentCodes, ticketCodes };
  }, [registry]);

  const renderConditionEditor = (branch, branchIndex, condition, conditionIndex) => {
    const baseFields = FIELD_OPTIONS[condition.source] || FIELD_OPTIONS.state;
    const dynamicFields = condition.source === "state"
      ? variableOptions.map((field) => ({ value: field, label: field }))
      : [];
    const fields = Array.from(new Map([
      ...baseFields,
      ...dynamicFields,
      condition.field ? { value: condition.field, label: condition.field } : null,
    ].filter(Boolean).map((field) => [field.value, field])).values());
    return (
      <div
        key={`${branch.id}-condition-${conditionIndex}`}
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr 150px 1fr auto", gap: 8, alignItems: "end", marginTop: 8 }}
      >
        <label style={{ fontSize: 12 }}>
          Source
          <select
            value={condition.source}
            onChange={(event) => {
              const nextSource = event.target.value;
              updateCondition(branchIndex, conditionIndex, {
                source: nextSource,
                field: FIELD_OPTIONS[nextSource]?.[0]?.value || "",
              });
            }}
            style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}
          >
            {SOURCE_OPTIONS.map((source) => (
              <option key={source.value} value={source.value}>{source.label}</option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          Field
          <select
            value={condition.field}
            onChange={(event) => updateCondition(branchIndex, conditionIndex, { field: event.target.value })}
            style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}
          >
            {fields.map((field) => <option key={field.value} value={field.value}>{field.label}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          Comparison
          <select
            value={condition.operator}
            onChange={(event) => updateCondition(branchIndex, conditionIndex, { operator: event.target.value })}
            style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}
          >
            {OPERATORS.map((operator) => (
              <option key={operator.value} value={operator.value}>{operator.label}</option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          Value
          <input
            list={`router-values-${id}`}
            disabled={!conditionNeedsValue(condition.operator)}
            value={condition.value || ""}
            onChange={(event) => updateCondition(branchIndex, conditionIndex, { value: event.target.value })}
            placeholder="success, paid, Chennai, 1000..."
            style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}
          />
        </label>
        <button
          type="button"
          onClick={() => updateBranch(branchIndex, {
            conditions: branch.conditions.filter((_, index) => index !== conditionIndex),
          })}
          disabled={branch.conditions.length <= 1}
          style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #fecaca", color: "#b91c1c", background: "#fff" }}
        >
          Remove
        </button>
      </div>
    );
  };

  const popup = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.35)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 10000,
      }}
      onClick={() => setShowEditPopup(false)}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: 12,
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          width: 980,
          maxWidth: "96vw",
          maxHeight: "90vh",
          overflow: "auto",
          padding: 18,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>Decision Router</h3>
        <p style={{ marginTop: 0, color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
          Choose what should happen next based on customer messages, selections, orders, payments,
          webhooks, tickets, appointments, CRM fields, or any saved answer. Start with a preset,
          then edit the output labels in plain language.
        </p>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
            What do you want to route based on?
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8 }}>
            {(WORKFLOW_ROUTER_PRESETS_HIDDEN_UNLESS_ACTIVE.has(draft.actionProfile)
              ? [...VISIBLE_TEMPLATE_KEYS, draft.actionProfile]
              : VISIBLE_TEMPLATE_KEYS
            ).map((key) => {
              const template = COMMON_TEMPLATES[key];
              if (!template) return null;
              return (
              <button
                key={key}
                type="button"
                onClick={() => applyTemplate(key)}
                style={{
                  textAlign: "left",
                  border: `1px solid ${draft.actionProfile === key ? "#7c3aed" : "#e2e8f0"}`,
                  background: draft.actionProfile === key ? "#f5f3ff" : "#fff",
                  color: "#334155",
                  borderRadius: 10,
                  padding: 10,
                  cursor: "pointer",
                  boxShadow: draft.actionProfile === key ? "0 4px 10px rgba(124,58,237,.12)" : "none",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 12, color: draft.actionProfile === key ? "#6d28d9" : "#0f172a" }}>
                  {template.label}
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, lineHeight: 1.35 }}>
                  {template.description}
                </div>
              </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, marginBottom: 12 }}>
          <label style={{ fontSize: 12 }}>
            Fallback output name
            <input
              value={draft.defaultLabel}
              onChange={(event) => updateDraft({ defaultLabel: event.target.value })}
              style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}
            />
          </label>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            Each output becomes a connection point on the node. Connect each output to the next message/action.
          </div>
          <button
            type="button"
            onClick={() => setShowAdvancedRules((prev) => !prev)}
            style={{
              padding: "7px 10px",
              borderRadius: 999,
              border: "1px solid #cbd5e1",
              background: showAdvancedRules ? "#f1f5f9" : "#fff",
              color: "#334155",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {showAdvancedRules ? "Hide advanced rules" : "Show advanced rules"}
          </button>
        </div>

        {showAdvancedRules ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 10 }}>
            <label style={{ fontSize: 12 }}>
              Router mode
              <select
                value={draft.routerMode}
                onChange={(event) => updateDraft({ routerMode: event.target.value })}
                style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}
              >
                {ROUTER_MODES.map((mode) => (
                  <option key={mode.id} value={mode.id}>{mode.label}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 12 }}>
              Match order
              <select
                value={draft.logic}
                onChange={(event) => updateDraft({ logic: event.target.value })}
                style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}
              >
                <option value="first_match">Use first matching output</option>
              </select>
            </label>
          </div>
        ) : null}

        {incomingSource ? (
          <div style={{ fontSize: 12, color: "#475569", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, marginBottom: 12 }}>
            Previous node detected: <b>{incomingSource.type}</b>
            {incomingSource.data?.flowName ? ` (${incomingSource.data.flowName})` : ""}
          </div>
        ) : null}

        {draft.branches.map((branch, branchIndex) => {
          const template = COMMON_TEMPLATES[draft.actionProfile] || COMMON_TEMPLATES.generic_action;
          const selectedPresetIndex = template.branches.findIndex(
            (preset) => branchConditionKey(preset) === branchConditionKey(branch)
          );
          return (
          <div
            key={branch.id}
            style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 12, background: "#f8fafc" }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "220px 1fr auto", gap: 8, alignItems: "end" }}>
              <label style={{ fontSize: 12 }}>
                Output name
                <input
                  value={branch.label}
                  onChange={(event) => updateBranch(branchIndex, { label: event.target.value })}
                  style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}
                />
              </label>
              <label style={{ fontSize: 12 }}>
                When should this output run?
                <select
                  value={selectedPresetIndex >= 0 ? String(selectedPresetIndex) : "custom"}
                  onChange={(event) => {
                    if (event.target.value !== "custom") applyBranchPreset(branchIndex, event.target.value);
                  }}
                  style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}
                >
                  {template.branches.map((preset, index) => (
                    <option key={preset.id} value={index}>{preset.label} - {preset.description}</option>
                  ))}
                  <option value="custom">Custom rule</option>
                </select>
              </label>
              <button
                type="button"
                onClick={() => updateDraft({ branches: draft.branches.filter((_, index) => index !== branchIndex) })}
                style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #fecaca", color: "#b91c1c", background: "#fff" }}
              >
                Remove
              </button>
            </div>

            <label style={{ display: "block", fontSize: 12, marginTop: 8 }}>
              Client note
              <input
                value={branch.description}
                onChange={(event) => updateBranch(branchIndex, { description: event.target.value })}
                placeholder="Example: Ask customer to choose another seat"
                style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}
              />
            </label>

            {showAdvancedRules ? (
              <div style={{ marginTop: 8, fontSize: 11, color: "#64748b" }}>
                Rule: {branchSummary(branch)}
              </div>
            ) : null}

            {showAdvancedRules ? (
              <div style={{ marginTop: 10, borderTop: "1px dashed #cbd5e1", paddingTop: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 8, alignItems: "end", marginBottom: 8 }}>
                  <label style={{ fontSize: 12 }}>
                    Multiple rules
                    <select
                      value={branch.match}
                      onChange={(event) => updateBranch(branchIndex, { match: event.target.value })}
                      style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}
                    >
                      <option value="all">All must match</option>
                      <option value="any">Any can match</option>
                    </select>
                  </label>
                  <div style={{ fontSize: 11, color: "#64748b" }}>
                    Advanced mode is for teams that need exact source, field, operator, and value control.
                  </div>
                </div>
                {(branch.conditions || []).map((condition, conditionIndex) => (
                  renderConditionEditor(branch, branchIndex, condition, conditionIndex)
                ))}
                <button
                  type="button"
                  onClick={() => addCondition(branchIndex)}
                  style={{ marginTop: 8, padding: "6px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff" }}
                >
                  Add another rule
                </button>
              </div>
            ) : null}
          </div>
          );
        })}

        <datalist id={`router-values-${id}`}>
          {["true", "false", "success", "failed", "paid", "pending", "cancelled", "completed", "Chennai", "1000"].map((value) => (
            <option key={value} value={value} />
          ))}
          {registryTemplates.appointmentCodes.map(([code]) => <option key={`appointment-${code}`} value={code} />)}
          {registryTemplates.ticketCodes.map(([code]) => <option key={`ticket-${code}`} value={code} />)}
        </datalist>

        {loadingRegistry ? (
          <div style={{ color: "#64748b", fontSize: 12, marginBottom: 8 }}>Loading platform outcome suggestions...</div>
        ) : null}
        {error ? <div style={{ color: "#b91c1c", fontSize: 12, marginBottom: 8 }}>{error}</div> : null}

        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <button
            type="button"
            onClick={addOutputBranch}
            style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff" }}
          >
            Add output branch
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={() => setShowEditPopup(false)} style={{ padding: "8px 12px" }}>Cancel</button>
            <button type="button" onClick={handleSave} style={{ padding: "8px 12px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 6 }}>
              Save router
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        style={{
          background: selected ? "#faf5ff" : "#f5f3ff",
          border: `1px solid ${selected ? "#7c3aed" : "#ddd6fe"}`,
          borderRadius: 10,
          padding: "10px 18px 10px 10px",
          width: 250,
          minHeight: branchLayout.minHeight,
          boxShadow: selected ? "0 4px 8px rgba(124, 58, 237, 0.25)" : "0 2px 4px rgba(0,0,0,0.1)",
          position: "relative",
          overflow: "visible",
        }}
      >
        <div
          style={{ position: "absolute", top: 8, right: 8, cursor: "pointer", fontSize: 16, color: "#6d28d9", fontWeight: "bold" }}
          onClick={() => setShowMenu(!showMenu)}
        >
          ...
        </div>
        {showMenu && (
          <div ref={menuRef} style={{ position: "absolute", top: 24, right: 8, background: "white", border: "1px solid #ddd", borderRadius: 4, boxShadow: "0 2px 8px rgba(0,0,0,0.15)", zIndex: 2 }}>
            <div style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, borderBottom: "1px solid #eee" }} onClick={openEdit}>Edit router</div>
            <div style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12 }} onClick={() => data.onDelete?.(id)}>Delete</div>
          </div>
        )}

        <Handle
          type="target"
          position={Position.Left}
          style={{ top: "50%", background: "#7c3aed", width: 12, height: 12 }}
        />

        <div style={{ pointerEvents: "none" }}>
          <div style={{ fontWeight: 700, color: "#6d28d9", fontSize: 12, marginBottom: 4 }}>
            Decision Router
          </div>
          <div style={{ fontSize: 11, color: "#475569", marginBottom: 8 }}>
            {ROUTER_MODES.find((mode) => mode.id === router.routerMode)?.label || "Smart branching"}
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            {router.branches.map((branch) => (
              <div
                key={branch.id}
                title={branch.description || branchSummary(branch)}
                style={{
                  height: branchLayout.rowSlot,
                  display: "flex",
                  alignItems: "center",
                  fontSize: 10,
                  color: "#334155",
                  background: "#fff",
                  border: "1px solid #e9d5ff",
                  borderRadius: 6,
                  padding: "4px 6px",
                  boxSizing: "border-box",
                  marginBottom: 0,
                }}
              >
                {branch.label}
              </div>
            ))}
            <div
              style={{
                height: branchLayout.rowSlot,
                display: "flex",
                alignItems: "center",
                fontSize: 10,
                color: "#64748b",
                background: "#fff",
                border: "1px dashed #cbd5e1",
                borderRadius: 6,
                padding: "4px 6px",
                boxSizing: "border-box",
              }}
            >
              {router.defaultLabel || "Default / fallback"}
            </div>
          </div>
        </div>

        {router.branches.map((branch, branchIndex) => (
          <Handle
            key={`handle-${branch.id}`}
            type="source"
            position={Position.Right}
            id={`${id}-branch-${branch.id}`}
            style={branchHandleStyle(branchLayout.branchHandleTop(branchIndex))}
          />
        ))}
        <Handle
          type="source"
          position={Position.Right}
          id={`${id}-default`}
          style={branchHandleStyle(branchLayout.defaultHandleTop(), "#64748b")}
        />
      </div>
      {showEditPopup ? createPortal(popup, document.body) : null}
    </>
  );
};
